
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FaSearch, FaEye, FaRupeeSign, FaFileAlt, FaClock, FaCheckCircle } from "react-icons/fa";
import WithdrawalDetailsModal from "../components/WithdrawalDetailsModal";
import { FiUser } from "react-icons/fi";

const Withdrawals = () => {
  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(25);

  const [totalRequests, setTotalRequests] = useState(0);

  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);

  const [stats, setStats] = useState({
  totalRequests: 0,
  pendingRequests: 0,
  completedRequests: 0,
});

  const getPaginationNumbers = () => {
    const pages = [];
    const total = totalPages;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    if (page > 3) pages.push("...");

    for (let i = page - 1; i <= page + 1; i++) {
      if (i > 1 && i < total) pages.push(i);
    }

    if (page < total - 2) pages.push("...");

    pages.push(total);

    return pages;
  };

  // Fetch data from API
  const fetchWithdrawals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `/api/v1/app/wallet/withdrawal-requests?page=${page}&limit=${limit}&status=${selectedStatus}&search=${debouncedSearch}`
      );


      if (res.data.status === "OK") {
        const data = res.data.data?.withdrawalRequests || [];

        setWithdrawals(data);
        setFiltered(data);

        setTotalPages(res.data.data?.totalPages || 1);
        setTotalRequests(res.data.data?.totalRequests || 0);
      }
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, selectedStatus, limit]); // FIXED: include page here

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals,debouncedSearch, selectedStatus, limit]);



  useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(search);
  }, 500);

  return () => clearTimeout(timer);
}, [search]);

const fetchStats = async () => {
  try {
    const res = await axios.get(
      "/api/v1/app/withdrawal/admin/stats"
    );

    if (res.data.status === "OK") {
      setStats(res.data.data);
    }
  } catch (err) {
    console.error("Stats error:", err);
  }
};

useEffect(() => {
  fetchStats();
}, []);


  

  const getStatusColor = (status) => {
    return "px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-[var(--primary-solid)] to-[var(--primary-dark)] text-white border-2 border-gray-200";
  };

  // Pagination buttons
  const nextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };
  const prevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 bg-white">
        <div className="w-8 h-8 rounded-full animate-spin border-4 border-gray-200 border-t-[var(--primary-solid)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6 text-[var(--black-text)]">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-[var(--primary-solid)]">
          Withdrawal Requests
        </h1>
        <p className="text-[var(--black-text)]/70 text-sm">
          Manage user withdrawal requests and payments
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-2 border-blue-500/20 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-blue-400 uppercase tracking-wider">
                Total Requests
              </p>
              <p className="text-2xl font-bold text-blue-400">
        {stats.totalRequests}
              </p>
            </div>
            <div className="text-3xl text-blue-400">
              <FaFileAlt />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-2 border-orange-500/20 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-orange-400 uppercase tracking-wider">
                Pending
              </p>
              <p className="text-2xl font-bold text-orange-400">
{stats.pendingRequests}              </p>
            </div>
            <div className="text-3xl text-orange-400">
              <FaClock />
            </div>
          </div>
        </div>



        <div className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-2 border-purple-500/20 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-purple-400 uppercase tracking-wider">
                Completed
              </p>
              <p className="text-2xl font-bold text-purple-400">
                {stats.completedRequests}
              </p>
            </div>
            <div className="text-3xl text-purple-400">
              <FaCheckCircle />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
        <h2 className="text-base font-semibold mb-4">Filters</h2>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative w-full sm:w-[70%]">
            <FaSearch className="absolute left-3 top-3 text-[var(--primary-solid)]" />
            <input
              type="text"
              placeholder="Search by user name, email, or amount..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-[var(--black-text)] focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
            />
          </div>

          <div className="w-full sm:w-[30%]">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-200 bg-white text-[var(--black-text)] rounded-lg focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
            >
              <option value="all">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="COMPLETED">Completed</option>
              <option value="REJECTED">Rejected</option>
              {/* <option value="CANCELLED">Cancelled</option> */}
            </select>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm">

        <h2 className="text-lg font-semibold mb-4">
          Withdrawal Requests ({filtered.length})
        </h2>

        {filtered.length > 0 ? (
          <div className="flex flex-col gap-4">
            {filtered.map((w) => (
              <div
                key={w._id}
                className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border-2 border-gray-200 rounded-lg bg-white hover:shadow-md transition"
              >
                <div className="flex items-center gap-3">

                  <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center">
                    {w.user?.profileImage &&
                      w.user.profileImage !== "default-profile.jpg" ? (
                      <img
                        src={w.user.profileImage}
                        alt={w.user?.fullName || "User"}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/default-avatar.png";
                        }}
                      />
                    ) : (
                      <FiUser className="text-gray-500 text-xl" />
                    )}
                  </div>

                  <div>
                    <p className="font-semibold text-[var(--black-text)]">{w.user?.fullName}</p>
                    <p className="text-sm text-[var(--black-text)]/70">{w.user?.email}</p>
                  </div>
                </div>

                <div className="mt-3 sm:mt-0 flex flex-col sm:flex-row items-center gap-3">
                  <p className="text-lg font-bold text-[var(--black-text)]">₹{w.amount}</p>
                  <span className={getStatusColor(w.status)}>{w.status}</span>
                  <button
                    onClick={() => setSelectedWithdrawal(w)}
                    className="px-4 py-1 bg-gradient-to-r from-[var(--primary-solid)] to-[var(--primary-dark)] text-white border-2 border-gray-200 rounded-lg flex items-center gap-1 hover:shadow-md transition"
                  >
                    <FaEye /> View
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center py-8 text-[var(--black-text)]/70">
            No withdrawal requests found.
          </p>
        )}

        {/* Dynamic Pagination */}
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            disabled={page === 1}
            onClick={prevPage}
            className={`px-4 py-2 rounded-lg border-2 ${page === 1
                ? "border-gray-300 text-gray-400 cursor-not-allowed"
                : "border-gray-200 text-[var(--black-text)] hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white"
              }`}
          >
            Prev
          </button>

          {getPaginationNumbers(page, totalPages).map((num, index) => (
            <button
              key={index}
              disabled={num === "..."}
              onClick={() => typeof num === "number" && setPage(num)}
              className={`px-3 py-1 rounded-lg border-2 text-sm ${num === page
                  ? "bg-gradient-to-r from-[var(--primary-solid)] to-[var(--primary-dark)] text-white border-gray-200"
                  : num === "..."
                    ? "border-transparent text-[var(--black-text)] cursor-default"
                    : "border-gray-200 text-[var(--black-text)] hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white"
                }`}
            >
              {num}
            </button>
          ))}

          <button
            disabled={page === totalPages}
            onClick={nextPage}
            className={`px-4 py-2 rounded-lg border-2 ${page === totalPages
                ? "border-gray-300 text-gray-400 cursor-not-allowed"
                : "border-gray-200 text-[var(--black-text)] hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white"
              }`}
          >
            Next
          </button>
          <div className="w-full sm:w-auto">
  <select
    value={limit}
    onChange={(e) => {
      setLimit(Number(e.target.value));
      setPage(1);
    }}
    className="w-full px-4 py-2 border-2 border-gray-200 bg-white text-[var(--black-text)] rounded-lg focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
  >
    <option value={10}>10 Rows</option>
    <option value={25}>25 Rows</option>
    <option value={50}>50 Rows</option>
    <option value={100}>100 Rows</option>
  </select>
</div>
        </div>
        
      </div>

      {selectedWithdrawal && (
        <WithdrawalDetailsModal
          withdrawal={selectedWithdrawal}
          onClose={() => setSelectedWithdrawal(null)}
          refreshData={() => {
        fetchWithdrawals();
        fetchStats();
    }}
        />
      )}
    </div>
  );
};

export default Withdrawals;

